import { createSlice } from '@reduxjs/toolkit';
import { t } from 'i18next';
import { showSuccess } from '../../app/utils/sweetAlert';
import { showSuccessToast } from '../../app/utils/tostify';
import {
	addUser,
	deleteUser,
	editUser,
	GetDefaultNotification,
	getSalesPersonList,
	getSettingUserList,
	getSupervisorsList,
	getUserDetails,
	tenantBranches
} from '../apis/usersAPIs';

interface userState {
	loading: boolean;
	error: any;
	notificationTypes: Array<any>;
	notificationValues: Array<any>;
	tenantBranches: Array<any>;
	userList: Array<any>;
	currentPage: number;
	count: number;
	userDetails: object;
	userWholeDetails: object;
	userRoleInfo: object;
	branchId: any;
	supervisorId: any;
	supervisorList: Array<any>;
	salesPersonList: Array<any>;
	searchKey: string;
	filter: any;
}

const initialState: userState = {
	loading: false,
	error: '',
	notificationTypes: [],
	notificationValues: [],
	tenantBranches: [],
	userList: [],
	currentPage: 1,
	count: 0,
	userDetails: {},
	userRoleInfo: {},
	branchId: '',
	supervisorList: [],
	supervisorId: '',
	userWholeDetails: {},
	salesPersonList: [],
	searchKey: '',
	filter: null
};

const usersSlice = createSlice({
	name: 'role',
	initialState,
	reducers: {
		handlePagination(state, action: any) {
			state.currentPage = action.payload;
		},
		handleSearchKey(state, action: any) {
			state.searchKey = action.payload;
		},
		handleFilter(state, action: any) {
			state.filter = action.payload;
		}
	},
	extraReducers: (builder) => {
		builder.addCase(GetDefaultNotification.pending, (state: userState) => {
			state.loading = true;
			state.error = '';
		});
		builder.addCase(
			GetDefaultNotification.fulfilled,
			(state: userState, action: any) => {
				state.loading = false;
				state.error = '';
				state.notificationTypes = action?.payload?.notificationTypes;
			}
		);
		builder.addCase(GetDefaultNotification.rejected, (state: userState) => {
			state.loading = false;
		});
		builder.addCase(tenantBranches.pending, (state: userState) => {
			state.loading = true;
			state.error = '';
			state.tenantBranches = [];
		});
		builder.addCase(
			tenantBranches.fulfilled,
			(state: userState, action: any) => {
				state.loading = false;
				state.error = '';
				state.tenantBranches = action?.payload?.data;
			}
		);
		builder.addCase(tenantBranches.rejected, (state: userState) => {
			state.loading = false;
		});
		builder.addCase(getSettingUserList.pending, (state: userState) => {
			state.loading = true;
			state.error = '';
			state.userList = [];
			state.userDetails = {};
			state.userRoleInfo = {};
			state.userWholeDetails = {};
			state.branchId = '';
			state.supervisorId = '';
			state.notificationValues = [];
			state.salesPersonList = [];
		});
		builder.addCase(
			getSettingUserList.fulfilled,
			(state: userState, action: any) => {
				state.loading = false;
				state.error = '';
				state.userList = action?.payload?.data?.list;
				state.count = action?.payload?.data?.count;
			}
		);
		builder.addCase(getSettingUserList.rejected, (state: userState) => {
			state.loading = false;
		});
		builder.addCase(addUser.pending, (state: userState) => {
			state.loading = true;
			state.error = '';
			state.userList = [];
		});
		builder.addCase(addUser.fulfilled, (state: userState, action: any) => {
			state.loading = false;
			state.error = '';
			showSuccessToast({ message: t(action.payload.message) });
		});
		builder.addCase(addUser.rejected, (state: userState) => {
			state.loading = false;
		});
		builder.addCase(getUserDetails.pending, (state) => {
			state.loading = true;
			state.branchId = '';
			state.supervisorId = '';
			state.userRoleInfo = {};
			state.userWholeDetails = {};
		});
		builder.addCase(getUserDetails.fulfilled, (state, action: any) => {
			const {
				user_id,
				role_id,
				branch_id,
				notifications,
				supervisor_id
			} = action.payload.data;
			state.userDetails = user_id;
			state.userRoleInfo = role_id;
			state.loading = false;
			state.branchId = branch_id ? branch_id : '';
			state.notificationValues = notifications;
			state.userWholeDetails = action.payload.data;
			if (supervisor_id?._id) {
				state.supervisorId = supervisor_id?._id; //if require than use full object currently only need of id
			}
		});
		builder.addCase(getUserDetails.rejected, (state) => {
			state.loading = false;
		});
		builder.addCase(getSupervisorsList.pending, (state) => {
			state.loading = true;
			state.branchId = '';
			state.supervisorId = '';
		});
		builder.addCase(getSupervisorsList.fulfilled, (state, action: any) => {
			state.loading = false;
			state.supervisorList = action.payload.data;
		});
		builder.addCase(getSupervisorsList.rejected, (state) => {
			state.loading = false;
		});
		builder.addCase(editUser.pending, (state) => {
			state.loading = true;
			state.branchId = '';
			state.supervisorId = '';
		});
		builder.addCase(editUser.fulfilled, (state, action: any) => {
			state.loading = false;
			showSuccess({ message: t(action.payload.message) });
		});
		builder.addCase(editUser.rejected, (state) => {
			state.loading = false;
		});
		builder.addCase(deleteUser.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(deleteUser.fulfilled, (state, action: any) => {
			state.loading = false;
			showSuccessToast({ message: t(action.payload.message) });
		});
		builder.addCase(deleteUser.rejected, (state) => {
			state.loading = false;
		});
		builder.addCase(getSalesPersonList.pending, (state) => {
			state.loading = true;
			state.branchId = '';
			state.supervisorId = '';
			state.salesPersonList = [];
		});
		builder.addCase(getSalesPersonList.fulfilled, (state, action: any) => {
			state.loading = false;
			state.salesPersonList = action.payload.data;
		});
		builder.addCase(getSalesPersonList.rejected, (state) => {
			state.loading = false;
		});
	}
});

export const { handlePagination, handleSearchKey, handleFilter } =
	usersSlice.actions;
export default usersSlice.reducer;
